import React, { useState } from 'react';
import { RESOURCE_EMOJIS, getDiceColor } from '../shared/constants';
import ResourceRadar from './ResourceRadar';
import { styles } from '../styles/StatsPanel.styles';

const DICE_PROBABILITIES = [
  { roll: 2,  combos: 1,  proba: '2.78%',  dots: 1 },
  { roll: 3,  combos: 2,  proba: '5.56%',  dots: 2 },
  { roll: 4,  combos: 3,  proba: '8.33%',  dots: 3 },
  { roll: 5,  combos: 4,  proba: '11.11%', dots: 4 },
  { roll: 6,  combos: 5,  proba: '13.89%', dots: 5 },
  { roll: 7,  combos: 6,  proba: '16.67%', dots: 6 },
  { roll: 8,  combos: 5,  proba: '13.89%', dots: 5 },
  { roll: 9,  combos: 4,  proba: '11.11%', dots: 4 },
  { roll: 10, combos: 3,  proba: '8.33%',  dots: 3 },
  { roll: 11, combos: 2,  proba: '5.56%',  dots: 2 },
  { roll: 12, combos: 1,  proba: '2.78%',  dots: 1 },
];

export default function StatsPanel({ statistics, settlements }) {
  const [showDiceModal, setShowDiceModal] = useState(false);

  if (!statistics) return null;

  const { per_dice_value, per_resource, any_resource } = statistics;
  const hasStats = settlements && Object.keys(settlements).length > 0;

  return (
    <div>
      {/* Dice probability modal */}
      {showDiceModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDiceModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h4 style={styles.modalTitle}>Dice Roll Probabilities</h4>
              <button style={styles.modalClose} onClick={() => setShowDiceModal(false)}>
                {'\u2715'}
              </button>
            </div>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Roll</th>
                    <th style={styles.th}>Combos</th>
                    <th style={styles.th}>Prob.</th>
                    <th style={{ ...styles.th, width: 90 }}>Visual</th>
                  </tr>
                </thead>
                <tbody>
                  {DICE_PROBABILITIES.map((d) => {
                    const color = getDiceColor(d.roll);
                    const isSeven = d.roll === 7;
                    return (
                      <tr key={d.roll} style={isSeven ? { backgroundColor: 'var(--tip-warning-bg)' } : {}}>
                        <td style={styles.td}>
                          <span style={{
                            fontWeight: 700,
                            color,
                            fontSize: 14,
                          }}>{d.roll}</span>
                        </td>
                        <td style={{ ...styles.td, fontVariantNumeric: 'tabular-nums' }}>
                          {d.combos}/36
                        </td>
                        <td style={{ ...styles.td, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {d.proba}
                        </td>
                        <td style={styles.td}>
                          <div style={styles.dotsBar}>
                            <div style={{
                              ...styles.dotsFill,
                              width: `${(d.dots / 6) * 100}%`,
                              backgroundColor: color,
                            }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!hasStats ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>&#127922;</div>
          <p style={styles.emptyText}>Click on a position on the board to place a settlement.</p>
        </div>
      ) : (
        <>
          {/* Resources per dice value */}
          <div style={styles.section}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Roll</th>
                    <th style={styles.th}>Resources</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(per_dice_value).map(([value, resources]) => (
                    <tr key={value}>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.diceValue,
                          color: getDiceColor(value),
                          fontWeight: 700,
                        }}>{value}</span>
                      </td>
                      <td style={styles.td}>
                        {resources.map((r, i) => (
                          <span key={i} style={{
                            ...styles.resourceTag,
                            backgroundColor: r.blocked ? 'var(--card-inner-bg)' : r.color + '18',
                            color: r.blocked ? 'var(--text-disabled)' : r.color,
                            borderColor: r.blocked ? 'var(--border-main)' : r.color + '44',
                            textDecoration: r.blocked ? 'line-through' : 'none',
                          }}>
                            {r.text}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per resource probabilities */}
          <div style={styles.section}>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Resource</th>
                    <th style={styles.th}>Prob.</th>
                    <th style={styles.th}>Rate</th>
                    <th style={{ ...styles.th, width: 100 }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(per_resource).sort((a, b) => (b[1].rate || 0) - (a[1].rate || 0)).map(([code, info]) => {
                    const isBlocked = info.blocked;
                    const blockedStyle = isBlocked ? { textDecoration: 'line-through', color: 'var(--text-disabled)' } : {};
                    return (
                      <tr key={code} style={isBlocked ? { opacity: 0.55 } : {}}>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ ...styles.resColorDot, backgroundColor: isBlocked ? 'var(--text-disabled)' : info.color }} />
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)', ...blockedStyle }}>{RESOURCE_EMOJIS[code] || ''} {info.text}</span>
                          </div>
                        </td>
                        <td style={{ ...styles.td, fontVariantNumeric: 'tabular-nums', ...blockedStyle }}>{info.proba.toFixed(3)}</td>
                        <td style={{ ...styles.td, fontVariantNumeric: 'tabular-nums', ...blockedStyle }}>{info.rate}/36</td>
                        <td style={styles.td}>
                          <div style={styles.percentCell}>
                            <div style={styles.percentBar}>
                              <div style={{ ...styles.percentFill, width: `${info.percentage}%`, backgroundColor: isBlocked ? 'var(--text-disabled)' : info.color }} />
                            </div>
                            <span style={{ ...styles.percentText, ...blockedStyle }}>{info.percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={styles.totalRow}>
                    <td style={styles.td}><strong style={{ color: 'var(--text-primary)' }}>Any</strong></td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{any_resource.proba.toFixed(3)}</td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{any_resource.rate}/36</td>
                    <td style={{ ...styles.td, fontWeight: 700 }}>{any_resource.percentage}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Resource balance radar */}
          <ResourceRadar perResource={per_resource} />

        </>
      )}

      <div style={styles.diceBtnRow}>
        <button
          style={styles.diceBtn}
          onClick={() => setShowDiceModal(true)}
          title="Dice probabilities"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="3" />
            <circle cx="8" cy="8" r="1.5" fill="var(--text-muted)" stroke="none" />
            <circle cx="16" cy="8" r="1.5" fill="var(--text-muted)" stroke="none" />
            <circle cx="8" cy="16" r="1.5" fill="var(--text-muted)" stroke="none" />
            <circle cx="16" cy="16" r="1.5" fill="var(--text-muted)" stroke="none" />
            <circle cx="12" cy="12" r="1.5" fill="var(--text-muted)" stroke="none" />
          </svg>
          <span style={styles.diceBtnText}>Dice probabilities</span>
        </button>
      </div>
    </div>
  );
}

